(() => {
  const SUPPORTS_POINTER = 'PointerEvent' in window;
  const MOVE_OPTS = { passive: false };

  function bindDragScroll(el, opts = {}) {
    if (!el || el.nodeType !== 1) return () => {};

    const multiplier     = Number.isFinite(opts.multiplier) ? opts.multiplier : 1.5;
    const activeClass    = typeof opts.activeClass === 'string' ? opts.activeClass : 'is-dragging';
    const touchAction    = typeof opts.touchAction === 'string' ? opts.touchAction : '';
    const debug          = !!opts.debug;
    const clickThreshold = Number.isFinite(opts.clickThreshold) ? opts.clickThreshold : 5;

    if (!el.style.touchAction) el.style.touchAction = touchAction;
    if (el.scrollWidth <= el.clientWidth) return () => {};

    let dragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let currentPointerId = null;
    let moved = false;
    let suppressNextClick = false; // <-- NEW: suppress only the *next* click if a drag happened

    const relX = (clientX) => clientX - el.getBoundingClientRect().left;

    // Helper: should we allow drag to start on this target?
    const isInteractive = (target) => {
      const elInteractive = target.closest?.(
        'button, input, select, label, summary, [role="button"], [data-no-drag]'
      );
      return !!elInteractive;
    };

    // --- Pointer Events path ---
    function onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (isInteractive(e.target)) return; // <-- NEW: let native click work

      currentPointerId = e.pointerId;
      dragging = true;
      moved = false;
      startX = relX(e.clientX);
      startScrollLeft = el.scrollLeft;
      el.classList.add(activeClass);
      el.style.userSelect = 'none';
      el.setPointerCapture?.(currentPointerId);
    }

    function onPointerMove(e) {
      if (!dragging || (currentPointerId !== null && e.pointerId !== currentPointerId)) return;
      e.preventDefault();
      const delta = relX(e.clientX) - startX;
      if (!moved && Math.abs(delta) > clickThreshold) moved = true;
      el.scrollLeft = startScrollLeft - delta * multiplier;
    }

    function endPointer() {
      if (!dragging) return;
      dragging = false;
      currentPointerId = null;
      el.classList.remove(activeClass);
      el.style.userSelect = '';
      if (moved) suppressNextClick = true; // <-- NEW: mark to suppress only the *next* click
      moved = false; // reset movement state
    }

    // Suppress only the next click after a drag; allow normal clicks otherwise
    function maybeSuppressClick(e) {
      if (!suppressNextClick) return;
      suppressNextClick = false; // <-- consume the suppression
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
    }

    // --- Mouse/Touch fallback ---
    let mouseDown = false;
    let startXMouse = 0;
    let startScrollLeftMouse = 0;

    function onMouseDown(e) {
      if (e.button !== 0) return;
      if (isInteractive(e.target)) return; // <-- NEW: let native click work
      mouseDown = true;
      moved = false;
      startXMouse = relX(e.clientX);
      startScrollLeftMouse = el.scrollLeft;
      el.classList.add(activeClass);
      el.style.userSelect = 'none';
    }

    function onMouseMove(e) {
      if (!mouseDown) return;
      e.preventDefault();
      const delta = relX(e.clientX) - startXMouse;
      if (!moved && Math.abs(delta) > clickThreshold) moved = true;
      el.scrollLeft = startScrollLeftMouse - delta * multiplier;
    }

    function onMouseUpOrLeave() {
      if (!mouseDown) return;
      mouseDown = false;
      el.classList.remove(activeClass);
      el.style.userSelect = '';
      if (moved) suppressNextClick = true; // <-- NEW
      moved = false;
    }

    function onTouchStart(e) {
      const t = e.touches && e.touches[0];
      if (!t) return;
      if (isInteractive(e.target)) return; // <-- NEW
      mouseDown = true;
      moved = false;
      startXMouse = relX(t.clientX);
      startScrollLeftMouse = el.scrollLeft;
      el.classList.add(activeClass);
      el.style.userSelect = 'none';
    }

    function onTouchMove(e) {
      if (!mouseDown) return;
      e.preventDefault();
      const t = e.touches && e.touches[0];
      if (!t) return;
      const delta = relX(t.clientX) - startXMouse;
      if (!moved && Math.abs(delta) > clickThreshold) moved = true;
      el.scrollLeft = startScrollLeftMouse - delta * multiplier;
    }

    function onTouchEnd() {
      onMouseUpOrLeave();
    }

    // Attach listeners
    if (SUPPORTS_POINTER) {
      el.addEventListener('pointerdown', onPointerDown);
      el.addEventListener('pointermove', onPointerMove, MOVE_OPTS);
      el.addEventListener('pointerup', endPointer);
      el.addEventListener('pointercancel', endPointer);
      el.addEventListener('lostpointercapture', endPointer);
      document.addEventListener('pointerup', endPointer);
    } else {
      el.addEventListener('mousedown', onMouseDown);
      document.addEventListener('mousemove', onMouseMove, MOVE_OPTS);
      document.addEventListener('mouseup', onMouseUpOrLeave);
      el.addEventListener('touchstart', onTouchStart, MOVE_OPTS);
      el.addEventListener('touchmove', onTouchMove, MOVE_OPTS);
      el.addEventListener('touchend', onTouchEnd);
      el.addEventListener('touchcancel', onTouchEnd);
    }

    // Click suppression (capture)
    el.addEventListener('click', maybeSuppressClick, true);

    // Unbind
    return function unbind() {
      if (SUPPORTS_POINTER) {
        el.removeEventListener('pointerdown', onPointerDown);
        el.removeEventListener('pointermove', onPointerMove, MOVE_OPTS);
        el.removeEventListener('pointerup', endPointer);
        el.removeEventListener('pointercancel', endPointer);
        el.removeEventListener('lostpointercapture', endPointer);
        document.removeEventListener('pointerup', endPointer);
      } else {
        el.removeEventListener('mousedown', onMouseDown);
        document.removeEventListener('mousemove', onMouseMove, MOVE_OPTS);
        document.removeEventListener('mouseup', onMouseUpOrLeave);
        el.removeEventListener('touchstart', onTouchStart, MOVE_OPTS);
        el.removeEventListener('touchmove', onTouchMove, MOVE_OPTS);
        el.removeEventListener('touchend', onTouchEnd);
        el.removeEventListener('touchcancel', onTouchEnd);
      }
      el.removeEventListener('click', maybeSuppressClick, true);
    };
  }

  function init() {
    const els = document.querySelectorAll('.featured-cases--container');
    els.forEach((el) => {
      bindDragScroll(el, { multiplier: 1.5, activeClass: 'active', debug: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.bindDragScroll = bindDragScroll;
})();
